/**
 * Lead create/update Zod schemas
 */

import { z } from 'zod'
import {
  LEAD_STATUSES,
  LEAD_SOURCES,
} from '../constants/statuses'

const linkedinUrlPattern = /^https?:\/\/(www\.)?linkedin\.com\/in\/[\w-]+\/?$/

export const createLeadSchema = z.object({
  linkedinUrl: z.string().url().regex(linkedinUrlPattern, 'Must be a valid LinkedIn profile URL'),
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  fullName: z.string().max(200).optional(),
  headline: z.string().max(500).optional(),
  company: z.string().max(200).optional(),
  title: z.string().max(200).optional(),
  location: z.string().max(200).optional(),
  industry: z.string().max(200).optional(),
  profileImageUrl: z.string().url().optional(),
  source: z.enum(LEAD_SOURCES as unknown as [string, ...string[]]).default('manual'),
  campaignId: z.string().optional(),
  tags: z.array(z.string().max(50)).max(20).default([]),
  notes: z.string().max(5000).optional(),
})

export const updateLeadSchema = z.object({
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  fullName: z.string().max(200).optional(),
  headline: z.string().max(500).optional(),
  company: z.string().max(200).optional(),
  title: z.string().max(200).optional(),
  location: z.string().max(200).optional(),
  industry: z.string().max(200).optional(),
  profileImageUrl: z.string().url().optional(),
  status: z.enum(LEAD_STATUSES as unknown as [string, ...string[]]).optional(),
  source: z.enum(LEAD_SOURCES as unknown as [string, ...string[]]).optional(),
  campaignId: z.string().nullable().optional(),
  icpScore: z.number().min(0).max(100).optional(),
  intentScore: z.number().min(0).max(100).optional(),
  compositeScore: z.number().min(0).max(100).optional(),
  enrichmentData: z.record(z.unknown()).optional(),
  enrichedAt: z.string().datetime().optional(),
  lastContactedAt: z.string().datetime().optional(),
  lastRepliedAt: z.string().datetime().optional(),
  tags: z.array(z.string().max(50)).max(20).optional(),
  notes: z.string().max(5000).optional(),
})

export const leadFilterSchema = z.object({
  status: z.enum(LEAD_STATUSES as unknown as [string, ...string[]]).optional(),
  source: z.enum(LEAD_SOURCES as unknown as [string, ...string[]]).optional(),
  campaignId: z.string().optional(),
  minScore: z.coerce.number().min(0).max(100).optional(),
  maxScore: z.coerce.number().min(0).max(100).optional(),
  search: z.string().max(200).optional(),
  tags: z.string().optional(), // comma-separated
})

export type CreateLeadInput = z.infer<typeof createLeadSchema>
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>
export type LeadFilterInput = z.infer<typeof leadFilterSchema>
